
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Language } from '../types';
import { t, TranslationKey } from '../locales';

interface WizardProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
  currentViewIndex: number; // 0=Home, 1=Meal, 2=Symptom, 3=Stats
}

interface TourStep {
  targetId: string;
  titleKey: TranslationKey;
  descKey: TranslationKey;
  position?: 'top' | 'bottom' | 'left' | 'right'; 
}

const Wizard: React.FC<WizardProps> = ({ isOpen, onClose, language, currentViewIndex }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [validSteps, setValidSteps] = useState<TourStep[]>([]);
  const [retryCount, setRetryCount] = useState(0);

  // Define potential steps per view
  const allStepsByView: Record<number, TourStep[]> = {
    0: [ // Home View
        { targetId: 'tour-streak', titleKey: 'tour_home_streak_title', descKey: 'tour_home_streak_desc', position: 'bottom' },
        { targetId: 'tour-analysis', titleKey: 'tour_home_analysis_title', descKey: 'tour_home_analysis_desc', position: 'bottom' },
        { targetId: 'header-kitchen-btn', titleKey: 'tour_header_kitchen_title', descKey: 'tour_header_kitchen_desc', position: 'bottom' },
        { targetId: 'header-health-btn', titleKey: 'tour_header_health_title', descKey: 'tour_header_health_desc', position: 'bottom' },
        { targetId: 'header-pharmacy-btn', titleKey: 'tour_header_pharmacy_title', descKey: 'tour_header_pharmacy_desc', position: 'bottom' },
        { targetId: 'home-smart-suggestions', titleKey: 'tour_smart_suggestions_title', descKey: 'tour_smart_suggestions_desc', position: 'top' },
        { targetId: 'home-record-meal-btn', titleKey: 'tour_home_record_meal_title', descKey: 'tour_home_record_meal_desc', position: 'top' },
        { targetId: 'home-record-symptom-btn', titleKey: 'tour_home_record_symptom_title', descKey: 'tour_home_record_symptom_desc', position: 'top' },
        { targetId: 'mic-button', titleKey: 'tour_mic_button_title', descKey: 'tour_mic_button_desc', position: 'top' },
        { targetId: 'tour-nav-meals', titleKey: 'tour_nav_meals_title', descKey: 'tour_nav_meals_desc', position: 'top' },
    ],
    1: [ // Meal View
        { targetId: 'tour-weekly-calendar', titleKey: 'tour_weekly_calendar_title', descKey: 'tour_weekly_calendar_desc', position: 'bottom' },
        { targetId: 'meal-calendar-toggle', titleKey: 'tour_calendar_toggle_title', descKey: 'tour_calendar_toggle_desc', position: 'bottom' },
        { targetId: 'tour-nav-symptoms', titleKey: 'symptoms', descKey: 'tour_nav_symptoms_desc', position: 'top' }, 
    ],
    2: [ // Symptom View
        { targetId: 'tour-weekly-calendar', titleKey: 'tour_weekly_calendar_title', descKey: 'tour_weekly_calendar_desc', position: 'bottom' },
        { targetId: 'symptom-calendar-toggle', titleKey: 'tour_calendar_toggle_title', descKey: 'tour_calendar_toggle_desc', position: 'bottom' },
        { targetId: 'tour-nav-stats', titleKey: 'tour_nav_stats_title', descKey: 'tour_nav_stats_desc', position: 'top' },
    ],
    3: [ // Stats View
        { targetId: 'tour-stats-diet', titleKey: 'tour_stats_diet_title', descKey: 'tour_stats_diet_desc', position: 'bottom' },
        { targetId: 'tour-stats-ranking', titleKey: 'tour_stats_ranking_title', descKey: 'tour_stats_ranking_desc', position: 'bottom' },
        { targetId: 'tour-stats-volume', titleKey: 'tour_stats_volume_title', descKey: 'tour_stats_volume_desc', position: 'top' },
        { targetId: 'tour-nav-home', titleKey: 'home', descKey: 'tour_nav_meals_desc', position: 'top' },
    ]
  };

  // Filter steps on mount/open to ensure targets exist
  useEffect(() => {
    if (isOpen) {
        const potentialSteps = allStepsByView[currentViewIndex] || [];
        const filtered = potentialSteps.filter(step => !!document.getElementById(step.targetId));
        
        setValidSteps(filtered);
        
        // If we found nothing but it's open, maybe wait a frame and retry once
        if (filtered.length === 0 && retryCount < 2) {
            const timer = setTimeout(() => setRetryCount(prev => prev + 1), 300);
            return () => clearTimeout(timer);
        }

        if (filtered.length === 0 && retryCount >= 2) {
            onClose();
        }
    } else {
        setRetryCount(0);
        setCurrentStepIndex(0);
    }
  }, [isOpen, currentViewIndex, retryCount]);

  const currentStep = validSteps[currentStepIndex];

  // Effect to calculate position of the target element
  useEffect(() => {
    if (!isOpen || !currentStep) return;

    const updatePosition = () => {
        const element = document.getElementById(currentStep.targetId);
        if (element) {
            const rect = element.getBoundingClientRect();
            setTargetRect(rect);
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            setTargetRect(null);
        }
    };

    const timer = setTimeout(updatePosition, 300);
    window.addEventListener('resize', updatePosition);
    return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, currentStep, currentStepIndex]);

  const handleNext = () => {
    if (currentStepIndex < validSteps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;
  if (validSteps.length === 0) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] overflow-hidden">
      <div 
        className={`absolute inset-0 transition-opacity duration-300 ${targetRect ? 'bg-transparent' : 'bg-black/30'}`} 
        onClick={onClose}
      ></div>

      {targetRect && (
          <div 
             className="absolute border-2 border-white/50 rounded-xl pointer-events-none transition-all duration-300 ease-in-out"
             style={{
                 top: targetRect.top - 4,
                 left: targetRect.left - 4,
                 width: targetRect.width + 8,
                 height: targetRect.height + 8,
                 boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4), 0 0 15px rgba(255, 255, 255, 0.3)'
             }}
          ></div>
      )}

      <div 
        className="absolute w-full max-w-xs p-5 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl transition-all duration-300 ease-out flex flex-col gap-3 animate-popIn border border-gray-100 dark:border-gray-700"
        style={{
            top: targetRect ? (currentStep.position === 'top' ? targetRect.top - 180 : targetRect.bottom + 20) : '50%',
            left: targetRect ? Math.min(Math.max(10, targetRect.left + (targetRect.width / 2) - 160), window.innerWidth - 330) : '50%',
            transform: targetRect ? 'none' : 'translate(-50%, -50%)',
        }}
      >
        {targetRect && (
            <div 
                className={`absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-gray-800 transform rotate-45 border-l border-t border-gray-100 dark:border-gray-700 ${
                    currentStep.position === 'top' ? 'bottom-[-8px] border-l-0 border-t-0 border-r border-b' : 'top-[-8px]'
                }`}
            ></div>
        )}

        <div className="relative z-10">
            <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-1">
                {t(language, currentStep.titleKey)}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                {t(language, currentStep.descKey)}
            </p>
        </div>

        <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 relative z-10">
            <button 
                onClick={onClose}
                className="text-xs font-bold text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
                {t(language, 'wizardSkip')}
            </button>
            
            <div className="flex items-center gap-3">
                 <div className="flex gap-1">
                    {validSteps.map((_, i) => (
                        <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === currentStepIndex ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                    ))}
                 </div>
                 <button 
                    onClick={handleNext}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-transform active:scale-95 shadow-md"
                 >
                    {currentStepIndex === validSteps.length - 1 ? t(language, 'wizardFinish') : t(language, 'wizardNext')}
                 </button>
            </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Wizard;
